import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaClipboardList, 
  FaUser, 
  FaCalendarAlt, 
  FaDownload, 
  FaEdit, 
  FaCheck, 
  FaTimes, 
  FaSpinner,
  FaFileAlt,
  FaArrowLeft,
  FaGraduationCap,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle
} from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const SubmissionList = () => {
  const { homeworkId } = useParams();
  const navigate = useNavigate();
  const [homework, setHomework] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [correctionData, setCorrectionData] = useState({
    feedback: '',
    grade: '',
    status: 'Aprovado'
  });
  const [correctionLoading, setCorrectionLoading] = useState(false);

  const fetchHomeworkSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      
      // Buscar dados da lição e suas submissões
      const response = await axios.get(`${config.apiURL}/teacher/homework/${homeworkId}/submissions`);
      
      setHomework(response.data.homework);
      setSubmissions(response.data.submissions);
    } catch (err) {
      console.error('Erro ao carregar submissões:', err);
      setError('Erro ao carregar submissões');
    } finally {
      setLoading(false);
    }
  }, [homeworkId]);

  useEffect(() => {
    fetchHomeworkSubmissions();
  }, [fetchHomeworkSubmissions]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Em Aberto':
        return 'bg-blue-100 text-blue-800';
      case 'Concluído':
        return 'bg-yellow-100 text-yellow-800';
      case 'Aprovado':
        return 'bg-green-100 text-green-800';
      case 'Atrasado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Em Aberto':
        return <FaClock className="w-4 h-4" />;
      case 'Concluído':
        return <FaExclamationTriangle className="w-4 h-4" />;
      case 'Aprovado':
        return <FaCheckCircle className="w-4 h-4" />;
      case 'Atrasado':
        return <FaExclamationTriangle className="w-4 h-4" />;
      default:
        return <FaFileAlt className="w-4 h-4" />;
    }
  };

  const startCorrection = (submission) => {
    setEditingSubmission(submission.id);
    setCorrectionData({
      feedback: submission.feedback || '',
      grade: submission.grade || '',
      status: submission.status === 'Concluído' ? 'Aprovado' : submission.status
    });
  };

  const cancelCorrection = () => {
    setEditingSubmission(null);
    setCorrectionData({
      feedback: '',
      grade: '',
      status: 'Aprovado'
    });
  };

  const submitCorrection = async () => {
    if (!editingSubmission) return;

    setCorrectionLoading(true);
    try {
      await axios.post(`${config.apiURL}/teacher/correct-submission/${editingSubmission}`, {
        feedback: correctionData.feedback,
        grade: parseFloat(correctionData.grade) || null,
        status: correctionData.status
      });

      // Recarregar submissões
      await fetchHomeworkSubmissions();
      
      setEditingSubmission(null);
      setCorrectionData({
        feedback: '',
        grade: '',
        status: 'Aprovado'
      });
    } catch (err) {
      console.error('Erro ao corrigir submissão:', err);
      alert('Erro ao salvar correção: ' + (err.response?.data?.error || err.message));
    } finally {
      setCorrectionLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCorrectionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSubmissionStats = () => {
    const total = submissions.length;
    const submitted = submissions.filter(s => s.status === 'Concluído' || s.status === 'Aprovado').length;
    const pending = submissions.filter(s => s.status === 'Em Aberto').length;
    const late = submissions.filter(s => s.status === 'Atrasado').length;
    const corrected = submissions.filter(s => s.status === 'Aprovado').length;

    return { total, submitted, pending, late, corrected };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando submissões...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => navigate('/homeworks')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Voltar para Lições
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stats = getSubmissionStats();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/homeworks')}
            className="mb-4 flex items-center space-x-2 text-blue-600 hover:text-blue-800"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>Voltar para Lições</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaClipboardList className="text-green-600" />
            <span>Entregas da Lição</span>
          </h1>
          
          {homework && (
            <div className="mt-4 p-4 bg-white rounded-lg shadow">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{homework.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Matéria:</span> {homework.subject}
                </div>
                <div>
                  <span className="font-medium">Vencimento:</span> {formatDate(homework.due_date)}
                </div>
                <div>
                  <span className="font-medium">Criada em:</span> {formatDate(homework.created_at)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">Total de Alunos</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.submitted}</div>
            <div className="text-sm text-gray-600">Entregaram</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pendentes</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.late}</div>
            <div className="text-sm text-gray-600">Atrasados</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.corrected}</div>
            <div className="text-sm text-gray-600">Corrigidos</div>
          </div>
        </div>

        {/* Lista de Submissões */}
        <div className="space-y-4">
          {submissions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <FaClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma submissão encontrada
              </h3>
              <p className="text-gray-600">
                As submissões dos alunos aparecerão aqui
              </p>
            </div>
          ) : (
            submissions.map((submission) => (
              <div key={submission.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 rounded-full p-3">
                        <FaUser className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {submission.student.name}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <FaGraduationCap className="h-4 w-4" />
                            <span>{submission.student.grade}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FaCalendarAlt className="h-4 w-4" />
                            <span>{submission.student.age || 'N/A'} anos</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(submission.status)}`}>
                          {getStatusIcon(submission.status)}
                          <span className="ml-1">{submission.status}</span>
                        </span>
                        {submission.submitted_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            Entregue em: {formatDate(submission.submitted_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Detalhes da Submissão */}
                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Arquivos */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Arquivos</h4>
                      <div className="space-y-2">
                        {submission.submission_file ? (
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <FaFileAlt className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-gray-700">Arquivo do Aluno</span>
                            </div>
                            <button
                              onClick={() => window.open(submission.submission_file, '_blank')}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              <FaDownload className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="p-3 bg-gray-50 rounded-lg text-center text-gray-500 text-sm">
                            Nenhum arquivo enviado
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Correção */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Correção</h4>
                      
                      {editingSubmission === submission.id ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nota (0-10)
                            </label>
                            <input
                              type="number"
                              name="grade"
                              min="0"
                              max="10"
                              step="0.1"
                              value={correctionData.grade}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Digite a nota"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              name="status"
                              value={correctionData.status}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Aprovado">Aprovado</option>
                              <option value="Para Revisão">Para Revisão</option>
                              <option value="Atrasado">Atrasado</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Feedback
                            </label>
                            <textarea
                              name="feedback"
                              rows="3"
                              value={correctionData.feedback}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Digite seu feedback para o aluno..."
                            />
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={submitCorrection}
                              disabled={correctionLoading}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                            >
                              {correctionLoading ? (
                                <>
                                  <FaSpinner className="animate-spin w-4 h-4" />
                                  <span>Salvando...</span>
                                </>
                              ) : (
                                <>
                                  <FaCheck className="w-4 h-4" />
                                  <span>Salvar</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={cancelCorrection}
                              disabled={correctionLoading}
                              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {submission.grade && (
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Nota:</span>
                              <span className="text-lg font-bold text-green-600">{submission.grade}</span>
                            </div>
                          )}
                          
                          {submission.feedback && (
                            <div>
                              <span className="font-medium text-gray-700">Feedback:</span>
                              <p className="text-gray-900 mt-1 text-sm">{submission.feedback}</p>
                            </div>
                          )}
                          
                          {submission.corrected_at && (
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Corrigido em:</span>
                              <span className="text-gray-900 text-sm">{formatDate(submission.corrected_at)}</span>
                            </div>
                          )}
                          
                          {submission.status === 'Concluído' || !submission.corrected_at ? (
                            <button
                              onClick={() => startCorrection(submission)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
                            >
                              <FaEdit className="w-4 h-4" />
                              <span>{submission.corrected_at ? 'Editar Correção' : 'Corrigir'}</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => startCorrection(submission)}
                              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <FaEdit className="w-4 h-4" />
                              <span>Editar Correção</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionList; 